import { Component, computed, forwardRef, inject, model, signal, viewChild } from '@angular/core';
import { FtpTransportViewerComponent } from '../ftp-transport-viewer/ftp-transport-viewer.component';
import { Transport, TransportType } from 'src/app/partnerportal/models/partner-onboarding';
import { CheckboxComponent } from 'src/app/controls/components/basic/checkbox/checkbox.component';
import { ControlValueAccessor, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgForm, Validator } from '@angular/forms';
import { PartnerOnboardingTransportService } from '../../../../services/transport-service';

@Component({
  selector: 'app-ftp-protocol-viewer',
  standalone: true,
  imports: [FormsModule, FtpTransportViewerComponent, CheckboxComponent],
  templateUrl: './ftp-protocol-viewer.component.html',
  styleUrl: './ftp-protocol-viewer.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FtpProtocolViewerComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FtpProtocolViewerComponent),
      multi: true
    }
  ],
})

export class FtpProtocolViewerComponent implements Validator, ControlValueAccessor {
  transportService = inject(PartnerOnboardingTransportService);
  form = viewChild<NgForm>('form');
  value = model<Transport[]>([]);
  disabled = signal<boolean>(false);

  private deferredStructuralEmitHandle: ReturnType<typeof setTimeout> | undefined;
  private deferredStructuralEmitValue: Transport[] | undefined;

  customerTransport = computed<Transport>(() => this.value()?.find(transport => transport.Type === TransportType.FTPCustomerEndpoint || transport.Type === TransportType.IntegratedFTP) ?? null);
  partnerTransport = computed<Transport>(() => this.value()?.find(transport => transport.Type === TransportType.FTPPartnerEndpoint) ?? null);

  private emitValue(newValue: Transport[], deferOnChange = false): void {
    this.value.set(newValue);
    if (!deferOnChange) {
      this.onChange(newValue);
      return;
    }

    // Structural changes (adding/removing nested ngModel controls) are registered with NgForm
    // after the current change detection cycle. Defer the CVA emit so parent validation sees
    // the final form structure.
    this.deferredStructuralEmitValue = newValue;
    if (this.deferredStructuralEmitHandle) {
      clearTimeout(this.deferredStructuralEmitHandle);
    }

    this.deferredStructuralEmitHandle = setTimeout(() => {
      this.deferredStructuralEmitHandle = undefined;
      const valueToEmit = this.deferredStructuralEmitValue ?? newValue;
      this.deferredStructuralEmitValue = undefined;

      this.form()?.form?.updateValueAndValidity({ emitEvent: false });
      this.onChange(valueToEmit);
    }, 0);
  }

  private async setTransportActive(type: TransportType, active: boolean): Promise<void> {
    const current = this.value() ?? [];
    const exists = current.some(t => t.Type === type);

    if (active) {
      if (exists) {
        return;
      }
      const generated = await this.transportService.generateTransport(type);
      this.emitValue([...current, generated], true);
      return;
    }

    if (!exists) {
      return;
    }
    this.emitValue(current.filter(t => t.Type !== type), true);
  }

  updateTransport(transport: Transport): void {
    console.log('Transport updated: ', transport);
    const current = this.value() ?? [];
    const next: Transport[] = [
      ...current.filter(t => transport.Type === TransportType.FTPPartnerEndpoint ? t.Type !== transport.Type : t.Type === TransportType.FTPPartnerEndpoint),
      transport
    ];
    this.emitValue(next);
  }

  customerActiveChanged(active: boolean): void {
    void this.setTransportActive(this.customerTransport()?.Type ?? TransportType.IntegratedFTP, active);
  }

  partnerActiveChanged(active: boolean): void {
    void this.setTransportActive(TransportType.FTPPartnerEndpoint, active);
  }


  /*
   * Validator interface methods
   */

  validate() {
    const frm = this.form()?.form;
    if (frm?.disabled ?? true) {
      return [];
    }
    return frm?.valid ? [] : [{ valid: false, message: 'Invalid in protocol viewer' }];
  }


  /*
   * ControlValueAccessor interface methods
   */

  writeValue(obj: Transport[]): void {
    this.value.set(obj);
  }

  onChange: (value: unknown) => void = () => { };
  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  onTouched: () => void = () => { };
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(!!isDisabled);
  }

}
